import { type FC, useCallback, useLayoutEffect, useRef } from "react";
import cx from "classnames";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Photo, Video } from "../../types";

import styles from "./PhotoViewerCaption.module.css";
import markdownStyles from "../AssistantMessage/MarkdownBlock.module.css";

const remarkPlugins = [remarkGfm];

const SCROLL_THRESHOLD = 2;

const updateScrollMask = (el: HTMLElement) => {
  const { scrollTop, scrollHeight, clientHeight } = el;
  const isOverflowing = scrollHeight > clientHeight;
  const atTop = scrollTop <= SCROLL_THRESHOLD;
  const atBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;

  el.classList.toggle(styles.maskBottom, isOverflowing && atTop && !atBottom);
  el.classList.toggle(styles.maskTop, isOverflowing && !atTop && atBottom);
  el.classList.toggle(styles.maskBoth, isOverflowing && !atTop && !atBottom);
};

type Props = {
  item: Photo | Video;
};

export const PhotoViewerCaption: FC<Props> = ({ item }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const title = item.title ?? item.caption;
  const description = item.title ? item.description : undefined;

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollMask(el);
  }, [title, description]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollMask(el);
  }, []);

  if (!title) return null;

  return (
    <div className={cx(styles.root, description && styles.hasDescription)}>
      <div
        ref={scrollRef}
        className={cx(description && styles.scroll)}
        onScroll={handleScroll}
      >
        <h1 className={styles.title}>{title}</h1>
        {description && (
          <div className={cx(styles.description, markdownStyles.markdown)}>
            <Markdown remarkPlugins={remarkPlugins}>{description}</Markdown>
          </div>
        )}
      </div>
    </div>
  );
};
